import { DefaultOptions, QueryClient } from "@tanstack/react-query";

import { isMaintenanceError } from "../utilities/maintenance";

/**
 * The app's QueryClient, shared with the test harness.
 *
 * It lives here rather than at the app entry point because a client built only
 * in src/index.tsx is not the client tests run against: renderWithProviders
 * builds its own, so behaviour configured there would be untested.
 *
 * Detecting a window is *not* this file's job -- that happens in
 * BaseApi.fetch, which every API call passes through, including the imperative
 * ones that never reach a query cache. What is left here is the retry policy,
 * which only React Query can decide.
 */
export function createQueryClient(
  defaultOptions: DefaultOptions = {}
): QueryClient {
  return new QueryClient({
    defaultOptions: {
      ...defaultOptions,
      queries: {
        // Retrying a planned window is pointless: the responder answers
        // instantly, so the default three attempts just triple request volume
        // against a rule that will keep saying no. Everything else keeps the
        // library default.
        retry: (failureCount: number, error: unknown) =>
          !isMaintenanceError(error) && failureCount < 3,
        ...defaultOptions.queries,
      },
    },
  });
}
